import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { HttpTypes } from '@/types';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { QueryKey, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { queryKeysFactory } from '@/lib/query-key-factory';

const CUSTOMER_SIGNUP_KEY = 'customer-signup' as const;
const CUSTOMER_REFRESH_TOKEN_KEY = 'customer-refresh-token' as const;
const CUSTOMER_LIST_ORDER_KEY = 'customer-list-order' as const;

const CUSTOMER_LIST_ORDER_QUERY_KEY = 'listOrder' as const;

export const customerListOrderQuery = queryKeysFactory(CUSTOMER_LIST_ORDER_KEY);
export const customerSignupQuery = queryKeysFactory(CUSTOMER_SIGNUP_KEY);
export const customerRefreshTokenQuery = queryKeysFactory(CUSTOMER_REFRESH_TOKEN_KEY);
export const customerListOrderQueryKeys = queryKeysFactory(CUSTOMER_LIST_ORDER_QUERY_KEY);

export const useCustomerSignup = (
  options?: UseMutationOptions<
    HttpTypes.StoreCustomerSignupResponse,
    FetchError,
    HttpTypes.StoreCustomerSignup
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.site.customer.signup(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useCustomerMe = (
  options?: UseMutationOptions<
    HttpTypes.StoreCustomerMeResponse,
    FetchError,
    HttpTypes.StoreCustomerMe
  >
) =>
  useMutation({
    mutationFn: (payload) => sdk.site.customer.me(payload.token),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });

export const useCustomerRefreshToken = (
  options?: UseMutationOptions<
    HttpTypes.StoreCustomerRefreshTokenResponse,
    FetchError,
    HttpTypes.StoreCustomerRefreshToken
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.site.customer.refreshToken(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useCustomerVerify = (
  options?: UseMutationOptions<
    HttpTypes.StoreCustomerVerifyEmailResponse,
    FetchError,
    {
      token: string;
    }
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.site.customer.verify(payload.token),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useCustomerListOrder = (
  token: string,
  params: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.StoreCustomerOrderListResponse,
      FetchError,
      HttpTypes.StoreCustomerOrderListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: customerListOrderQueryKeys.list(params),
    queryFn: async () => sdk.site.customer.listOrder(token, params),
    ...options,
  });

  return { ...data, ...rest };
};

export const useVerifyToken = (
  options?: UseMutationOptions<
    HttpTypes.StoreCustomerVerifyEmailResponse,
    FetchError,
    {
      token: string;
    }
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.site.customer.verifyToken(payload.token),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};
